<script setup>
import { computed } from 'vue';

/**
 * Totals across a split, as bars or as a share ring.
 *
 * A time series answers "when"; these answer "how much" and "how much of the whole". Drawing ten
 * packages as ten lines over a year is unreadable — the lines cross constantly and none of them
 * is the question being asked. Bars rank them; the ring shows the mix.
 *
 * Bars are horizontal on purpose: package names are long, and a vertical bar chart would either
 * clip them or turn the axis into rotated text.
 */
const props = defineProps({
  /** [{ key, label, revenue, units }]. */
  series: {
    type: Array,
    default: () => [],
  },
  /** 'bars' or 'share'. */
  mode: {
    type: String,
    default: 'bars',
  },
  /** 'revenue' or 'units'. */
  metric: {
    type: String,
    default: 'revenue',
  },
});

const PALETTE = [
  'var(--pfs-teal)',
  'var(--pfs-red)',
  'var(--pfs-orange)',
  'var(--teal-dark)',
  'var(--green-light)',
  'var(--gray-dark)',
  'var(--red-light)',
  'var(--orange-light)',
];

const isMoney = computed(() => props.metric === 'revenue');

const rows = computed(() => {
  const sorted = [...props.series].sort((a, b) => (b[props.metric] || 0) - (a[props.metric] || 0));
  return sorted.map((entry, index) => ({
    key: entry.key,
    label: entry.label,
    value: Number(entry[props.metric]) || 0,
    color: PALETTE[index % PALETTE.length],
  }));
});

const total = computed(() => rows.value.reduce((sum, row) => sum + row.value, 0));
const peak = computed(() => Math.max(1, ...rows.value.map(row => row.value)));

/**
 * Format a value in whichever measure is active.
 * @param {number} value Value.
 * @returns {string}
 */
function format(value) {
  return isMoney.value
    ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : value.toLocaleString('en-US');
}

/**
 * Share of the total, as a percentage.
 * @param {number} value Value.
 * @returns {number}
 */
function share(value) {
  return total.value > 0 ? (value / total.value) * 100 : 0;
}

// --- Share ring geometry -------------------------------------------------------------------
const SIZE = 200;
const RADIUS = 78;
const THICKNESS = 30;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * A donut rather than a pie: the hole carries the total, and arc LENGTH is easier to compare
 * than wedge area, which people systematically misjudge.
 */
const arcs = computed(() => {
  let offset = 0;
  return rows.value
    .filter(row => row.value > 0)
    .map(row => {
      const fraction = total.value > 0 ? row.value / total.value : 0;
      const arc = {
        key: row.key,
        label: row.label,
        color: row.color,
        value: row.value,
        percent: fraction * 100,
        dash: `${(fraction * CIRCUMFERENCE).toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`,
        offset: (-offset * CIRCUMFERENCE).toFixed(2),
      };
      offset += fraction;
      return arc;
    });
});
</script>

<template>
  <div :class="$style.wrapper">
    <p v-if="rows.length === 0" :class="$style.empty">
      Nothing sold in this window.
    </p>

    <!-- Bars -->
    <ul v-else-if="mode === 'bars'" :class="$style.bars">
      <li v-for="row in rows" :key="row.key" :class="$style.barRow">
        <span :class="$style.barLabel" :title="row.label">{{ row.label }}</span>
        <span :class="$style.barTrack">
          <span
            :class="$style.barFill"
            :style="{ width: `${Math.max(0.5, (row.value / peak) * 100)}%`, background: row.color }"
          />
        </span>
        <span :class="$style.barValue">{{ format(row.value) }}</span>
        <span :class="$style.barShare">{{ share(row.value).toFixed(1) }}%</span>
      </li>
    </ul>

    <!-- Share ring -->
    <div v-else :class="$style.shareLayout">
      <svg
        :viewBox="`0 0 ${SIZE} ${SIZE}`"
        :width="SIZE"
        :height="SIZE"
        :class="$style.ring"
        role="img"
        :aria-label="isMoney ? 'Share of revenue' : 'Share of registrations'"
      >
        <g :transform="`translate(${SIZE / 2} ${SIZE / 2}) rotate(-90)`">
          <circle
            v-for="arc in arcs"
            :key="arc.key"
            :r="RADIUS"
            cx="0"
            cy="0"
            fill="none"
            :stroke="arc.color"
            :stroke-width="THICKNESS"
            :stroke-dasharray="arc.dash"
            :stroke-dashoffset="arc.offset"
          />
        </g>
        <text :x="SIZE / 2" :y="SIZE / 2 - 4" text-anchor="middle" :class="$style.ringTotal">
          {{ format(total) }}
        </text>
        <text :x="SIZE / 2" :y="SIZE / 2 + 14" text-anchor="middle" :class="$style.ringCaption">
          {{ isMoney ? 'total' : 'registrations' }}
        </text>
      </svg>

      <ul :class="$style.shareLegend">
        <li v-for="arc in arcs" :key="arc.key" :class="$style.shareItem">
          <i :class="$style.swatch" :style="{ background: arc.color }" />
          <span :class="$style.shareLabel">{{ arc.label }}</span>
          <span :class="$style.shareValue">{{ format(arc.value) }}</span>
          <span :class="$style.sharePercent">{{ arc.percent.toFixed(1) }}%</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style module>
.wrapper {
  min-width: 0;
}
.bars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.barRow {
  display: grid;
  grid-template-columns: minmax(120px, 1.4fr) 3fr auto auto;
  gap: 12px;
  align-items: center;
  font-size: var(--font-size-lg);
}
.barLabel {
  color: var(--primary-font-dark-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.barTrack {
  height: 14px;
  border-radius: 3px;
  background: var(--background-color);
  overflow: hidden;
}
.barFill {
  display: block;
  height: 100%;
  border-radius: 3px;
  opacity: 0.85;
}
.barValue {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--primary-font-dark-color);
  white-space: nowrap;
}
.barShare {
  font-variant-numeric: tabular-nums;
  color: var(--faint-color);
  font-size: var(--font-size-md);
  white-space: nowrap;
  min-width: 46px;
  text-align: right;
}
.shareLayout {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  align-items: center;
}
.ring {
  flex: none;
}
.ringTotal {
  font-size: 17px;
  font-weight: 700;
  fill: var(--primary-font-dark-color);
}
.ringCaption {
  font-size: 10px;
  fill: var(--faint-color);
}
.shareLegend {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.shareItem {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 10px;
  align-items: center;
  font-size: var(--font-size-lg);
}
.swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.shareLabel {
  color: var(--primary-font-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shareValue {
  font-variant-numeric: tabular-nums;
  color: var(--primary-font-dark-color);
  white-space: nowrap;
}
.sharePercent {
  font-variant-numeric: tabular-nums;
  color: var(--faint-color);
  font-size: var(--font-size-md);
  min-width: 46px;
  text-align: right;
}
.empty {
  margin: 0;
  padding: 36px 0;
  text-align: center;
  color: var(--faint-color);
  font-size: var(--font-size-lg);
}
</style>
